'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ArrowClockwise, WarningCircle } from '@/components/icons'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  activateWaitingServiceWorkerAndReload,
  CHUNK_LOAD_RECOVERY_EVENT,
  isChunkLoadError,
  isChunkLoadRecoveryPending,
  recoverBrowserChunkLoadError,
} from '@/lib/observability/chunk-load-recovery'

interface ChunkLoadErrorBoundaryProps {
  children: ReactNode
}

interface ChunkLoadErrorBoundaryState {
  error: Error | null
  recoveryRequired: boolean
}

function ChunkLoadRecoveryFallback() {
  const t = useTranslations('ChunkLoadRecovery')

  return (
    <div className="bg-background flex h-full min-h-80 items-center justify-center p-6">
      <div
        className="border-border bg-card surface-lifted flex max-w-md flex-col items-center rounded-xl border p-6 text-center"
        data-testid="chunk-load-recovery"
        role="alert"
      >
        <div className="bg-destructive/10 mb-4 flex h-10 w-10 items-center justify-center rounded-full">
          <WarningCircle className="text-icon-error h-5 w-5" weight="regular" />
        </div>
        <h1 className="text-title-sm text-foreground">{t('title')}</h1>
        <p className="text-subtitle-sm text-muted-foreground mt-2 max-w-sm">{t('description')}</p>
        <Button
          className="mt-5"
          onClick={() => {
            void activateWaitingServiceWorkerAndReload()
          }}
          type="button"
        >
          <ArrowClockwise />
          {t('action')}
        </Button>
        <p className="text-body-xs text-muted-foreground mt-3 max-w-xs">{t('hint')}</p>
      </div>
    </div>
  )
}

export class ChunkLoadErrorBoundary extends Component<
  ChunkLoadErrorBoundaryProps,
  ChunkLoadErrorBoundaryState
> {
  state: ChunkLoadErrorBoundaryState = { error: null, recoveryRequired: false }

  static getDerivedStateFromError(error: Error): ChunkLoadErrorBoundaryState {
    return { error, recoveryRequired: isChunkLoadError(error) }
  }

  componentDidMount(): void {
    window.addEventListener(CHUNK_LOAD_RECOVERY_EVENT, this.requireRecovery)
    if (isChunkLoadRecoveryPending()) this.requireRecovery()
  }

  componentWillUnmount(): void {
    window.removeEventListener(CHUNK_LOAD_RECOVERY_EVENT, this.requireRecovery)
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      recoverBrowserChunkLoadError(error, 'react-error-boundary')
    }
  }

  private requireRecovery = (): void => {
    this.setState({ recoveryRequired: true })
  }

  render() {
    const { error, recoveryRequired } = this.state
    if (error && !isChunkLoadError(error)) throw error
    if (!recoveryRequired) return this.props.children
    return <ChunkLoadRecoveryFallback />
  }
}
